import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Mail,
  MapPin,
  Instagram,
  AtSign,
  Facebook,
  Youtube,
  Award,
  Rocket,
  MessageSquare,
  GraduationCap,
} from "lucide-react";
import PageShell from "../components/sections/PageShell";
import projects from "../data/projects";

const setMeta = (attr, key, value) => {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
};
const setLink = (rel, href) => {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const SERVICES = [
  {
    icon: Rocket,
    title: "Custom AI app builds",
    body:
      "Done-for-you AI agents, web apps, and landing pages delivered in weeks. Clear scope, clear price, target delivery window, working product at the end.",
    to: "/services/ai-app-development",
  },
  {
    icon: MessageSquare,
    title: "Custom AI chatbots",
    body:
      "Company-specific chatbots trained on your docs, tone, and workflows. For support, sales, onboarding, or internal Q&A.",
    to: "/front-office",
  },
  {
    icon: GraduationCap,
    title: "1-on-1 AI coaching",
    body:
      "$75/hour, 30-minute increments. Learn prompting, vibe coding, and how to ship real products with AI as your dev team.",
    to: "/#two-ways",
  },
];

const SOCIALS = [
  { id: "instagram", url: "https://www.instagram.com/gadgetguymedia/", Icon: Instagram, label: "Instagram" },
  { id: "facebook", url: "https://www.facebook.com/gadgetguymedia", Icon: Facebook, label: "Facebook" },
  { id: "threads", url: "https://www.threads.com/@gadgetguymedia", Icon: AtSign, label: "Threads" },
  { id: "youtube", url: "https://www.youtube.com/@gadgetguymedia", Icon: Youtube, label: "YouTube" },
];

export default function PortfolioPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    const title = "Robert · AI Builder & Founder, Gadget Guy Media — Portfolio";
    const desc = `Robert · AI Builder & Founder of Gadget Guy Media. ${projects.length} live AI apps shipped in weeks, not months. Google AI Professional Certified. Custom AI builds, chatbots, and 1-on-1 coaching.`;
    document.title = title;
    setMeta("name", "description", desc);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:type", "profile");
    setMeta("property", "og:url", "https://gadgetguymedia.com/portfolio");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", desc);
    setLink("canonical", "https://gadgetguymedia.com/portfolio");
    return () => setLink("canonical", "https://gadgetguymedia.com/");
  }, []);

  return (
    <PageShell testId="portfolio-page">
      {/* Hero — founder profile */}
      <section
        className="relative"
        style={{
          paddingTop: "clamp(48px, 8vh, 96px)",
          paddingBottom: "clamp(48px, 8vh, 96px)",
        }}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(1000px 500px at 15% 30%, rgba(11,60,220,0.16), transparent 70%), radial-gradient(700px 400px at 95% 5%, rgba(40,200,240,0.10), transparent 70%)",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-5 md:px-8 grid gap-10 md:grid-cols-[1fr_auto] items-center">
          <div>
            <div className="ggm-eyebrow mb-4" data-testid="portfolio-eyebrow">
              Founder profile
            </div>
            <h1
              className="font-condensed font-bold text-white"
              style={{
                fontSize: "clamp(2.5rem, 6vw, 4.5rem)",
                lineHeight: 1.02,
                letterSpacing: "-0.02em",
              }}
              data-testid="portfolio-name"
            >
              Robert.
            </h1>
            <p
              className="mt-3 ggm-gradient-text font-condensed font-bold"
              style={{ fontSize: "clamp(1.15rem, 2.4vw, 1.75rem)", lineHeight: 1.2 }}
            >
              AI Builder &amp; Founder, Gadget Guy Media
            </p>
            <p className="mt-5 font-body text-white/75 text-base md:text-lg leading-relaxed max-w-2xl">
              I ship custom AI apps in weeks, not months. No dev team, no six-month roadmap —
              just me and AI as the co-pilot, turning ideas into working products.{" "}
              {projects.length} live projects to date, all built and delivered by one person.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 font-body text-sm text-white/60">
              <span className="inline-flex items-center gap-1.5">
                <MapPin size={14} style={{ color: "var(--ggm-signal-cyan)" }} /> Gallatin, TN · Nashville metro
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Award size={14} style={{ color: "var(--ggm-signal-cyan)" }} /> Google AI Professional Certified
              </span>
            </div>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/#start-a-project" className="ggm-btn-primary" data-testid="portfolio-cta-book">
                Start a Project <ArrowUpRight size={16} />
              </Link>
              <Link to="/projects" className="ggm-btn-secondary" data-testid="portfolio-cta-projects">
                See all {projects.length} projects <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          {/* Robert photo — same treatment as homepage Founder tile */}
          <div className="shrink-0 mx-auto md:mx-0" style={{ width: "clamp(220px, 26vw, 280px)" }}>
            <div
              className="relative rounded-3xl overflow-hidden"
              data-testid="portfolio-photo"
              style={{
                aspectRatio: "1 / 1.15",
                background:
                  "linear-gradient(180deg, rgba(11,60,220,0.14), rgba(40,200,240,0.06))",
                border: "1px solid var(--ggm-border-subtle)",
                boxShadow: "var(--ggm-shadow-md)",
              }}
            >
              <img
                src="/brand/robert-studio.png"
                alt="Robert — founder of Gadget Guy Media"
                className="w-full h-full object-cover"
                loading="eager"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Ways to work */}
      <section
        className="relative"
        style={{ paddingBottom: "clamp(48px, 8vh, 96px)" }}
        data-testid="portfolio-services"
      >
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="ggm-eyebrow mb-4">Ways to work together</div>
          <h2
            className="font-condensed font-bold text-white uppercase mb-8"
            style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.5rem)", lineHeight: 1.05 }}
          >
            Three ways in.
          </h2>
          <div className="grid gap-5 md:grid-cols-3">
            {SERVICES.map((s) => (
              <Link
                key={s.title}
                to={s.to}
                className="group rounded-2xl p-6 hover:border-[var(--ggm-signal-cyan)]/40 transition-colors"
                style={{
                  background: "var(--ggm-surface)",
                  border: "1px solid var(--ggm-border-subtle)",
                }}
                data-testid={`portfolio-service-${s.title.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <s.icon size={22} style={{ color: "var(--ggm-signal-cyan)" }} />
                <h3 className="mt-4 font-condensed font-bold text-white text-lg group-hover:text-[var(--ggm-signal-cyan)] transition-colors">
                  {s.title}
                </h3>
                <p className="mt-2 font-body text-white/70 text-sm leading-relaxed">{s.body}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Point to canonical projects library */}
      <section
        className="relative"
        style={{ paddingBottom: "clamp(48px, 8vh, 96px)" }}
        data-testid="portfolio-projects-link"
      >
        <div className="max-w-6xl mx-auto px-5 md:px-8 grid gap-6 md:grid-cols-[1fr_auto] items-center">
          <div>
            <div className="ggm-eyebrow mb-3">The receipts</div>
            <h2
              className="font-condensed font-bold text-white uppercase"
              style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.5rem)", lineHeight: 1.05 }}
            >
              {projects.length} live projects. Every one shipped.
            </h2>
            <p className="mt-4 font-body text-white/70 max-w-xl">
              The full library — client work, personal builds, experiments — with case studies for each.
            </p>
          </div>
          <Link to="/projects" className="ggm-btn-primary shrink-0" data-testid="portfolio-view-projects">
            View all projects <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Credentials */}
      <section
        className="relative"
        style={{ paddingBottom: "clamp(48px, 8vh, 96px)" }}
        data-testid="portfolio-credentials"
      >
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="ggm-eyebrow mb-4">Credentials</div>
          <h2
            className="font-condensed font-bold text-white uppercase mb-8"
            style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.5rem)", lineHeight: 1.05 }}
          >
            Verified by Google.
          </h2>
          <div className="grid gap-5 md:grid-cols-2">
            <div
              className="rounded-2xl p-6"
              style={{ background: "var(--ggm-surface)", border: "1px solid var(--ggm-border-subtle)" }}
            >
              <div className="flex items-center gap-2">
                <Award size={16} style={{ color: "var(--ggm-signal-cyan)" }} />
                <span className="font-condensed uppercase tracking-widest text-[11px] text-white/60">
                  Google · Sep 2026
                </span>
              </div>
              <h3 className="mt-3 font-condensed font-bold text-white text-lg">
                Google AI Professional Certificate
              </h3>
              <p className="mt-2 font-body text-white/70 text-sm leading-relaxed">
                8 courses, 20+ hands-on labs. Prompt engineering, AI-powered software design, agentic workflows.
              </p>
            </div>
            <div
              className="rounded-2xl p-6"
              style={{ background: "var(--ggm-surface)", border: "1px solid var(--ggm-border-subtle)" }}
            >
              <div className="flex items-center gap-2">
                <Award size={16} style={{ color: "var(--ggm-signal-cyan)" }} />
                <span className="font-condensed uppercase tracking-widest text-[11px] text-white/60">
                  Google · Aug 2026
                </span>
              </div>
              <h3 className="mt-3 font-condensed font-bold text-white text-lg">Google AI Essentials</h3>
              <p className="mt-2 font-body text-white/70 text-sm leading-relaxed">
                5-course foundation in practical AI: everyday productivity, responsible use, and prompt patterns
                that work in real workflows.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Conversion */}
      <section
        className="relative"
        style={{
          paddingTop: "clamp(48px, 8vh, 96px)",
          paddingBottom: "clamp(64px, 10vh, 140px)",
          background:
            "radial-gradient(1000px 500px at 30% 20%, rgba(11,60,220,0.14), transparent 70%)",
        }}
      >
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
          <h2
            className="font-condensed font-bold text-white uppercase"
            style={{
              fontSize: "clamp(2rem, 4.5vw, 3.25rem)",
              lineHeight: 1.02,
              letterSpacing: "-0.02em",
            }}
          >
            Got Something In Your Head?
          </h2>
          <p
            className="mt-4 font-condensed font-bold ggm-gradient-text uppercase"
            style={{ fontSize: "clamp(1.25rem, 3vw, 2rem)", lineHeight: 1.15 }}
          >
            Let's turn it into something real.
          </p>
          <div className="mt-8 flex justify-center flex-wrap gap-4 items-center">
            <Link to="/#start-a-project" className="ggm-btn-primary" data-testid="portfolio-cta-book-2">
              Start a Project <ArrowUpRight size={16} />
            </Link>
            <a
              href="mailto:robert@gadgetguymedia.com"
              className="inline-flex items-center gap-2 font-condensed uppercase tracking-widest text-xs text-white/70 hover:text-[var(--ggm-signal-cyan)] transition-colors"
              style={{ transitionDuration: "var(--ggm-duration-ui)", minHeight: 44 }}
              data-testid="portfolio-cta-email"
            >
              <Mail size={13} /> robert@gadgetguymedia.com
            </a>
          </div>
          <div className="mt-8 flex items-center justify-center gap-5 text-white/50">
            {SOCIALS.map((s) => (
              <a
                key={s.id}
                href={s.url}
                target="_blank"
                rel="noreferrer"
                className="hover:text-[var(--ggm-signal-cyan)] transition-colors"
                aria-label={s.label}
                data-testid={`portfolio-social-${s.id}`}
              >
                <s.Icon size={18} />
              </a>
            ))}
          </div>
        </div>
      </section>
    </PageShell>
  );
}
